import {gql} from '@apollo/client';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import legacyRunModal from 'ui-library/legacyRunModal';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import {withLoadingScreen} from 'util/Promises';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';
import {embedInsightsIntoTagSchema} from '../data/EmbedInsightsActions';
import {
  TagSchemaDashboardLinkTableQuery,
  TagSchemaDashboardLinkTableQueryVariables,
} from './__generated/TagSchemaDashboardLinkTableQuery';
import DashboardLinkConfigModal from './DashboardLinkConfigModal';
import {
  DashboardLinkConfigTableDashboardEmbedded,
  DashboardLinkConfigTableErrored,
  DashboardLinkConfigTableLoading,
  DashboardLinkConfigTableNoneEmbedded,
} from './PresentationalDashboardLinkConfigTables';

type AnalyticsDashboard = ExtractByTypename<
  NonNullable<
    ExtractByTypename<
      TagSchemaDashboardLinkTableQuery['tagSchema'],
      'TagSchemaNode'
    >['analyticsDashboardLink']
  >['originalAnalyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsParameterConfig = AnalyticsDashboard['parameterConfigs'][number];

export const ERROR_LOADING_EMBEDDED_DASHBOARD_TEXT =
  'There was an error loading the dashboard embedded in this schema.';
export const NO_ACCESS_TO_EMBEDDED_DASHBOARD_TEXT =
  'You do not have access to the dashboard embedded in this schema.';

export const TAG_SCHEMA_DASHBOARD_LINK_TABLE_QUERY = gql`
  query TagSchemaDashboardLinkTableQuery($tagSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: INSIGHTS) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        ...AnalyticsDashboardLinkParentGraphQL
        analyticsDashboardLink {
          id
          originalAnalyticsDashboard {
            ... on AnalyticsDashboardNode {
              id
              name
              url
              parameterConfigs {
                ...AnalyticsParameterConfigGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('AnalyticsDashboardLinkParent').fragment}
  ${getFragmentWrapper('AnalyticsParameterValueTemplate').fragment}
  ${getFragmentWrapper('AnalyticsParameterConfig').fragment}
`;

export interface TagSchemaDashboardLinkConfigTableProps {
  tagSchemaId: string;
  isWritable: boolean;
}

export default function TagSchemaDashboardLinkConfigTable(
  props: TagSchemaDashboardLinkConfigTableProps
): JSX.Element {
  const tagSchemaDashboardQueryResult = useBenchlingQuery<
    TagSchemaDashboardLinkTableQuery,
    TagSchemaDashboardLinkTableQueryVariables
  >(TAG_SCHEMA_DASHBOARD_LINK_TABLE_QUERY, {
    variables: {tagSchemaId: props.tagSchemaId},
  });

  async function openDashboardLinkConfigModal(
    embeddedDashboard: AnalyticsDashboard | null,
    title: string,
    valueTemplates?: Array<AnalyticsParameterValueTemplateGraphQL>,
    focusConfig?: Pick<AnalyticsParameterConfig, 'id'>
  ): Promise<void> {
    assertAndContinue(props.isWritable, 'Unexpectedly trying to modify embedded dashboard as read-only user');

    const modalOutput = await legacyRunModal(DashboardLinkConfigModal, {
      embeddingType: ParameterType.BIOENTITY_LINK,
      title,
      embeddedDashboard,
      valueTemplates: valueTemplates ?? [],
      focusConfig,
    });

    if (modalOutput && modalOutput.dashboard) {
      await withLoadingScreen(
        embedInsightsIntoTagSchema(props.tagSchemaId, {
          analyticsDashboardLink: {
            originalAnalyticsDashboardId: modalOutput.dashboard.id,
            parameterValueTemplates: modalOutput.parameterValueTemplates,
          },
        })
      );
      /**
       * Force the table to refresh by issuing a network request for the newly embedded dashboard's details
       *
       * We found that we need to call this when embedding a dashboard in a tag schema. We believe it is
       * because the dashboard selector query does not include all attributes required by the
       * DashboardLinkTableQuery query (i.e. the dashboard's URL), and therefore the
       * DashboardLinkConfigTable does not update. To fix this, we force-refresh the query.
       *
       * TODO(BNCH-12369): Check whether Apollo 3 fixes this. Ideally we wouldn't need this method call,
       * because it incurs extra network fetches.
       */
      await tagSchemaDashboardQueryResult.refetch();
    }
  }

  async function removeEmbeddedDashboard(): Promise<void> {
    assertAndContinue(props.isWritable, 'Unexpectedly trying to remove embedded dashboard as read-only user');
    if (
      await runConfirmDeleteModal({
        typeName: 'embedded dashboard',
        message: 'Are you sure you want to remove the dashboard embedded in this schema?',
      })
    ) {
      await withLoadingScreen(embedInsightsIntoTagSchema(props.tagSchemaId, {analyticsDashboardLink: null}));
    }
  }

  // GraphQL query failed - show error message
  if (tagSchemaDashboardQueryResult.type === QueryResultType.ERROR) {
    return <DashboardLinkConfigTableErrored errorMessage={ERROR_LOADING_EMBEDDED_DASHBOARD_TEXT} />;
  }

  // GraphQL query loading - show throbber
  if (tagSchemaDashboardQueryResult.type === QueryResultType.PENDING) {
    return <DashboardLinkConfigTableLoading />;
  }

  const tagSchema = tagSchemaDashboardQueryResult.data.tagSchema;

  // Tag schema is inaccessible (should never happen as the user is already on the tag schema page, but
  // catch it just in case, and also for type safety)
  if (isInaccessible(tagSchema)) {
    return <DashboardLinkConfigTableErrored errorMessage={ERROR_LOADING_EMBEDDED_DASHBOARD_TEXT} />;
  }

  const embeddedDashboard = tagSchema.analyticsDashboardLink?.originalAnalyticsDashboard ?? null;

  // The current user does not have access to the embedded dashboard. Show an error as well as a remove
  // button if the user has write access to the tag schema.
  if (embeddedDashboard && isInaccessible(embeddedDashboard)) {
    return (
      <DashboardLinkConfigTableErrored
        removeEmbeddedDashboard={props.isWritable ? removeEmbeddedDashboard : undefined}
        errorMessage={NO_ACCESS_TO_EMBEDDED_DASHBOARD_TEXT}
      />
    );
  }

  // There's no dashboard embedded in the tag schema.
  if (embeddedDashboard == null) {
    return (
      <DashboardLinkConfigTableNoneEmbedded
        addEmbeddedDashboard={
          props.isWritable
            ? (focusConfig?: Pick<AnalyticsParameterConfig, 'id'>) => {
                openDashboardLinkConfigModal(embeddedDashboard, 'Add embedded dashboard', [], focusConfig);
              }
            : undefined
        }
      />
    );
  }

  return (
    <DashboardLinkConfigTableDashboardEmbedded
      embeddingType={ParameterType.BIOENTITY_LINK}
      embeddedDashboard={embeddedDashboard}
      parameterValueTemplates={tagSchema.analyticsDashboardLink?.parameterValueTemplates ?? []}
      removeEmbeddedDashboard={props.isWritable ? removeEmbeddedDashboard : undefined}
      editEmbeddedDashboard={
        props.isWritable
          ? (focusConfig?: Pick<AnalyticsParameterConfig, 'id'>) => {
              openDashboardLinkConfigModal(
                embeddedDashboard,
                'Edit embedded dashboard',
                tagSchema.analyticsDashboardLink?.parameterValueTemplates ?? [],
                focusConfig
              );
            }
          : undefined
      }
    />
  );
}
